import { useEffect, useState } from 'react';
import { useTranslation } from '@eon/i18n';
import { DataTable, PageHeader, Pagination, type Column } from '@eon/ui';
import { hooks, isActiveSession } from '@eon/api-client';
import type { ChargingSession } from '@eon/types';
import { formatDate, formatDuration, formatKW, formatKWh } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import { SessionStatusBadge } from '@/components/status/StatusBadges';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function liveDurationSeconds(session: ChargingSession, nowMs: number): number | null {
  if (isActiveSession(session.status) && session.startedAt) {
    const start = Date.parse(session.startedAt);
    if (!Number.isNaN(start)) {
      return Math.max(0, Math.floor((nowMs - start) / 1000));
    }
  }
  return session.durationSeconds ?? null;
}

export function SessionsPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useAdminSessions({ page, pageSize, ...sortParams });

  const hasActive = Boolean(list.data?.data?.some((s) => isActiveSession(s.status)));
  useEffect(() => {
    if (!hasActive) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasActive]);

  const columns: Column<ChargingSession>[] = [
    { key: 'started', header: t('session.started'), sortable: true, sortKey: 'started', sortValue: (s) => s.startedAt, cell: (s) => formatDate(s.startedAt) },
    { key: 'user', header: t('session.user'), cell: (s) => s.userName ?? s.idTag ?? '—' },
    { key: 'device', header: t('session.device'), cell: (s) => s.deviceDisplayName ?? '—' },
    { key: 'group', header: t('session.group'), cell: (s) => s.groupName ?? s.organizationName ?? '—' },
    { key: 'connector', header: t('device.connectors'), sortable: true, sortKey: 'connector', sortValue: (s) => s.connectorId, cell: (s) => `#${s.connectorId}` },
    { key: 'energy', header: t('session.energy'), sortable: true, sortValue: (s) => s.totalEnergyKWh, cell: (s) => formatKWh(s.totalEnergyKWh) },
    { key: 'power', header: t('session.power'), cell: (s) => formatKW(s.meanPowerKW) },
    {
      key: 'duration',
      header: t('session.duration'),
      sortable: true,
      sortValue: (s) => liveDurationSeconds(s, nowMs),
      cell: (s) => formatDuration(liveDurationSeconds(s, nowMs)),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      cell: (s) => <SessionStatusBadge status={String(s.status)} />,
    },
  ];

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title={t('nav.sessions')}
        description={list.data ? `${totalCount.toLocaleString()} ${t('nav.sessions')}` : undefined}
      />
      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(s) => s.id}
        sort={sort}
        onSortChange={onSortChange}
      />
      {list.data && totalCount > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}
    </>
  );
}
